import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Combobox, Label, Stack, Typography } from "@altimateai/lego";

import { getAvailableDatabases } from "@/helpers/apis";

interface SourceSitePickerProps {
  /** Page-context data_store id. Used both as the query key for
   * ``/data_stores/{id}/available_databases`` (which already groups siblings
   * by ``instance_id`` on the backend) and as the request's ``datastore_id``
   * later in the dialog so the backend can resolve the chosen sibling. */
  datastoreId: number;
  /** Site name of the sibling whose PAT the new site will reuse. */
  value: string;
  onChange: (next: string) => void;
  /** Disabled when the user opts for a fresh PAT — picker becomes irrelevant. */
  disabled: boolean;
  /** Skip the network call when the parent dialog is closed. */
  enabled: boolean;
}

const AVAILABLE_DATABASES_QUERY_KEY = "GET_AVAILABLE_DATABASES";

const SourceSitePicker = ({
  datastoreId,
  value,
  onChange,
  disabled,
  enabled,
}: SourceSitePickerProps): JSX.Element => {
  // Sibling sites = other Tableau sites under the same instance as this
  // datastore. ``used_databases`` is exactly that list (the chips at the
  // top of the page render from the same response). Reuses the parent
  // page's query key so React Query serves this from cache.
  const { data: availableDatabases } = useQuery({
    queryKey: [AVAILABLE_DATABASES_QUERY_KEY, datastoreId],
    queryFn: () => getAvailableDatabases(String(datastoreId)),
    enabled,
    staleTime: 30_000,
  });

  const options = useMemo(
    () =>
      (availableDatabases?.used_databases ?? []).map(name => ({
        value: name,
        label: name,
      })),
    [availableDatabases]
  );

  return (
    <Stack direction="column" className="al-gap-1">
      <Label htmlFor="tableau-source-site">Reuse PAT from</Label>
      <Typography variant="body" size="xs" className="al-text-gray-500">
        Pick an existing site whose PAT should authenticate this new site. Disabled when you supply
        a different PAT below.
      </Typography>
      <Combobox
        id="tableau-source-site"
        placeholder="Select a site"
        options={options}
        value={value}
        onChange={next => {
          if (typeof next === "string" && next) {
            onChange(next);
          }
        }}
        buttonProps={{
          className: "!al-w-full al-justify-between",
          disabled: disabled || options.length === 0,
        }}
      />
    </Stack>
  );
};

export default SourceSitePicker;
